import {
    SelectItemProps as AriakitSelectItemProps,
    SelectPopoverProps,
    Role,
    Select,
    useSelectContext,
    Combobox,
    ComboboxProps,
    ComboboxListProps,
    SelectListProps,
    SelectItemCheck,
    SelectGroupLabelProps,
    useStoreState
} from '@ariakit/react'
import {
    StyledSelectArrow,
    StyledSelectComboboxList,
    StyledSelectComboboxPopover,
    StyledSelectGroupLabel,
    StyledSelectItem,
    StyledSelectItemContent,
    StyledSelectList,
    StyledSelectPopover,
    StyledSelectTrigger,
    StyledSelectVirtualList
} from '@components/ui/select-atoms/style'
import {FC, forwardRef, ReactNode} from 'react'
import {ButtonProps} from '@components/ui/button/Button.tsx'
import {ChevronDownIcon} from '@components/ui/icon/Icon.tsx'
import {raise} from '@utilities/helpers.ts'
import {Scrollbar} from '@components/ui/scrollbar/Scrollbar.tsx'
import {ScrollArea} from '@components/ui/scroll-area/ScrollArea.tsx'
import {InputText} from '@/components/commons/input-text/InputText'
import {VirtuosoProps} from 'react-virtuoso'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useTheme} from 'styled-components'

export const SelectPopover = ({sameWidth = true, ...props}: SelectPopoverProps) => (
    <StyledSelectPopover gutter={4} sameWidth={sameWidth} {...props} />
)

export const SelectComboboxPopover = ({sameWidth = true, ...props}: SelectPopoverProps) => (
    <StyledSelectComboboxPopover gutter={4} sameWidth={sameWidth} {...props} />
)

export const SelectList = (props: SelectListProps) => (
    <ScrollArea overflowX="hidden" scrollbar={<Scrollbar />}>
        <StyledSelectList {...props} />
    </ScrollArea>
)

export const SelectGroupLabel = (props: SelectGroupLabelProps) => <StyledSelectGroupLabel {...props} />

export const SelectComboboxList = (props: ComboboxListProps) => (
    <ScrollArea overflowX="hidden" scrollbar={<Scrollbar />}>
        <StyledSelectComboboxList {...props} />
    </ScrollArea>
)

export const SelectVirtualList = <T,>(props: VirtuosoProps<T, unknown>) => <StyledSelectVirtualList {...props} />

interface SelectItemProps extends AriakitSelectItemProps {
    hasItemCheckbox?: boolean
    selectItemContentClassName?: string
}
export const SelectItem: FC<SelectItemProps> = ({children, hasItemCheckbox, selectItemContentClassName, ...props}) => (
    <StyledSelectItem {...props}>
        {hasItemCheckbox && <SelectItemCheck style={{flexShrink: 0}} />}
        <StyledSelectItemContent className={selectItemContentClassName}>{children}</StyledSelectItemContent>
    </StyledSelectItem>
)

export const SelectComboboxInput = forwardRef<HTMLInputElement, ComboboxProps>((props, ref) => (
    <Combobox autoSelect render={<InputText ref={ref} type="text" inputSize="sm" />} {...props} />
))

export interface SelectTriggerProps extends Omit<ButtonProps, 'children'> {
    placeholder?: ReactNode
    displayValue: (value: string | string[]) => ReactNode
    hasError?: boolean
    isLoading?: boolean
}

export const SelectTrigger = forwardRef<HTMLSelectElement, SelectTriggerProps>(
    ({placeholder, displayValue, hasError = false, isLoading = false, ...rest}, ref) => {
        const selectContext = useSelectContext() ?? raise('SelectTrigger must be in a SelectProvider')
        const value = useStoreState(selectContext, 'value')
        const isOpen = useStoreState(selectContext, 'open')
        const children = value.length >= 1 ? displayValue(value) : undefined
        const {palette} = useTheme()

        return (
            <Select
                render={
                    <Role.select
                        ref={ref}
                        render={
                            <StyledSelectTrigger
                                $hasChildren={!!children}
                                type="button"
                                variant="secondary"
                                $hasError={hasError}
                                {...rest}
                            >
                                {children || placeholder}
                                {isLoading ? (
                                    <Spinner size={12} color={palette.neutral['400']} />
                                ) : (
                                    <StyledSelectArrow $isOpen={isOpen} render={<ChevronDownIcon />} />
                                )}
                            </StyledSelectTrigger>
                        }
                    />
                }
            />
        )
    }
)
